import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ShieldCheck,
    Heart,
    Sprout,
    Eye,
    ArrowRight,
    Users,
    Sparkles,
} from "lucide-react";
import SectionReveal from "@/components/SectionReveal";
import SectionHeading from "@/components/SectionHeading";
import OrganicShapes from "@/components/OrganicShapes";
import { CATEGORY_LIST } from "@/lib/mockData";

const STORY_IMG =
    "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?auto=format&fit=crop&w=1400&q=80";

const PILLARS = [
    {
        icon: Sprout,
        title: "Ecological responsibility",
        body: "Native species, soil-aware planting, and year-long maintenance — never just a photo-op.",
        tint: "bg-seedh-surface-green",
    },
    {
        icon: Eye,
        title: "Operational transparency",
        body: "Public IDs and photo timelines for every tree, food drive, and child welfare kit.",
        tint: "bg-seedh-surface-blue",
    },
    {
        icon: Heart,
        title: "Donor dignity",
        body: "Recognition, certificates, and quiet honesty — even when something doesn't go to plan.",
        tint: "bg-seedh-surface-beige",
    },
    {
        icon: Users,
        title: "Community-led",
        body: "Our drives are co-organised with local volunteers, schools, and partner shopkeepers.",
        tint: "bg-seedh-surface-green",
    },
];

export default function About() {
    return (
        <main data-testid="about-page" className="bg-white pt-32 pb-24 relative overflow-hidden">
            <OrganicShapes variant="mixed" />

            <section className="max-w-7xl mx-auto px-6 md:px-10 relative">
                <SectionHeading
                    eyebrow="About Seedh Welfare Society"
                    heading={["A foundation built on a", "promise of traceability."]}
                    italicIndex={1}
                    description="Seedh is an Udaipur-based welfare society working across three quiet, persistent causes — tree plantation, food drives, and child welfare. We're small, we're careful, and we publish everything we do."
                />
            </section>

            {/* Our Story */}
            <section
                data-testid="about-story"
                className="mt-24 md:mt-32 relative"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    <SectionReveal className="lg:col-span-6">
                        <div className="relative">
                            <div className="aspect-[4/5] rounded-3xl overflow-hidden border border-seedh-border">
                                <img
                                    src={STORY_IMG}
                                    alt="Volunteers planting saplings"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl border border-seedh-border p-5 shadow-[0_30px_60px_-30px_rgba(4,88,134,0.35)] max-w-[200px]">
                                <div className="text-[0.65rem] uppercase tracking-[0.22em] text-seedh-brown">
                                    Founded
                                </div>
                                <div className="font-display text-4xl text-seedh-blue mt-1">
                                    2024
                                </div>
                                <div className="text-xs text-seedh-muted mt-2 leading-relaxed">
                                    Registered in Udaipur, Rajasthan
                                </div>
                            </div>
                        </div>
                    </SectionReveal>

                    <div className="lg:col-span-6">
                        <SectionHeading
                            eyebrow="Our story"
                            heading={["A welfare society that started with", "one question."]}
                            italicIndex={1}
                        />
                        <SectionReveal delay={0.15}>
                            <p className="mt-7 text-base text-seedh-text leading-relaxed">
                                After watching too many donations vanish into
                                generic reports, our founders asked a simple
                                question:{" "}
                                <span className="font-display-italic text-seedh-green text-lg">
                                    why can't every contribution carry a public
                                    ID?
                                </span>
                            </p>
                            <p className="mt-5 text-base text-seedh-muted leading-relaxed">
                                Seedh was built around that idea — first for tree
                                plantation, then for food drives, and now
                                for child welfare. Every record we publish
                                is permanent, public, and free to verify.
                            </p>
                        </SectionReveal>
                    </div>
                </div>
            </section>

            {/* Mission */}
            <section className="mt-24 md:mt-32 relative bg-seedh-surface-blue py-20 md:py-28">
                <OrganicShapes variant="blue" />
                <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
                    <SectionHeading
                        eyebrow="Our mission"
                        heading={[
                            "Support real causes —",
                            "and publish every step.",
                        ]}
                        italicIndex={1}
                        description="To run welfare drives across trees, food, and education with measurable, traceable, and publicly verifiable outcomes — never marketing language."
                    />

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {PILLARS.map((p, i) => (
                            <motion.div
                                key={p.title}
                                initial={{ opacity: 0, y: 28 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{
                                    delay: i * 0.08,
                                    duration: 0.8,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                                whileHover={{ y: -6 }}
                                className={`card-base p-6 ${p.tint}`}
                                data-testid={`pillar-${i}`}
                            >
                                <span className="w-10 h-10 rounded-full bg-white border border-seedh-border flex items-center justify-center">
                                    <p.icon className="w-5 h-5 text-seedh-blue" />
                                </span>
                                <div className="font-display text-xl text-seedh-text mt-4 leading-tight">
                                    {p.title}
                                </div>
                                <p className="text-sm text-seedh-muted mt-2 leading-relaxed">
                                    {p.body}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Focus Areas */}
            <section className="py-24 md:py-32 bg-white relative overflow-hidden">
                <OrganicShapes variant="green" />
                <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
                    <SectionHeading
                        eyebrow="Our focus areas"
                        heading={["Three causes,", "one transparent system."]}
                        italicIndex={1}
                        description="We deliberately keep our scope narrow so we can be honest about every outcome."
                    />

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
                        {CATEGORY_LIST.map((c, i) => (
                            <SectionReveal delay={i * 0.08} key={c.key}>
                                <Link
                                    to={`/donate?cause=${c.key}`}
                                    className={`block card-base p-7 ${c.tintBg} h-full group`}
                                >
                                    <span className="w-12 h-12 rounded-full bg-white border border-seedh-border flex items-center justify-center">
                                        <c.icon className={`w-6 h-6 ${c.accentText}`} />
                                    </span>
                                    <h3 className="font-display text-2xl text-seedh-text mt-5 leading-tight">
                                        {c.label}
                                    </h3>
                                    <p className="text-sm text-seedh-muted mt-3 leading-relaxed">
                                        {c.key === "trees" &&
                                            "Native saplings, public IDs, year-long maintenance and replacement records."}
                                        {c.key === "food" &&
                                            "Cooked meals and dry ration packets distributed via local outreach partners."}
                                        {c.key === "education" &&
                                            "Books, stationery, and school supplies — distributed with school-level logs."}
                                    </p>
                                    <div className="mt-6 inline-flex items-center gap-1.5 text-sm text-seedh-blue group-hover:translate-x-1 transition-transform">
                                        Support this cause
                                        <ArrowRight className="w-4 h-4" />
                                    </div>
                                </Link>
                            </SectionReveal>
                        ))}
                    </div>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="h-px w-full" style={{ background: "rgba(4,88,134,0.18)" }} />
            </div>

            {/* Community Impact */}
            <section className="py-24 md:py-32 relative bg-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
                    <SectionReveal>
                        <span className="eyebrow justify-center">Community impact</span>
                    </SectionReveal>
                    <SectionReveal delay={0.05}>
                        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-seedh-blue mt-6 tracking-tight">
                            Built with{" "}
                            <span className="font-display-italic text-seedh-green">
                                neighbours, not influencers.
                            </span>
                        </h2>
                    </SectionReveal>
                    <SectionReveal delay={0.15}>
                        <p className="mt-6 text-base text-seedh-muted max-w-2xl mx-auto leading-relaxed">
                            Our drives are co-organised with local volunteers,
                            schoolteachers, anganwadi workers, and shopkeepers
                            in Udaipur. Every contribution funds real,
                            on-the-ground work in our own neighbourhood.
                        </p>
                    </SectionReveal>
                    <SectionReveal delay={0.22}>
                        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                to="/donate"
                                className="btn-primary rounded-full px-7 py-4 text-sm font-semibold inline-flex items-center justify-center gap-2"
                            >
                                Choose a Cause
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/gallery"
                                className="btn-outline rounded-full px-7 py-4 text-sm font-semibold inline-flex items-center justify-center gap-2"
                            >
                                <Sparkles className="w-4 h-4" />
                                See impact gallery
                            </Link>
                        </div>
                    </SectionReveal>
                </div>
            </section>
        </main>
    );
}
